import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import ActionBar from "../../../components/Shared/ActionBar"
import AdminLayout from "../../../components/Shared/AdminLayout"
import Container from "../../../components/Shared/Container"
import CategoryCard from "../../../components/Admin/Categories/CategoryCard"
import CategoryList from "../../../components/Admin/Categories/CategoryList"
import CategoryViewModal from "../../../components/Admin/Categories/CategoryViewModal"
import Pagination from "../../../components/Shared/Pagination"

import * as adminHandlers from "../redux/handlers"


function Categories(props) {
    const dispatch = useDispatch();
    const { categories, categoriesTotal, loading, error } = useSelector(state => state.admin);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState("list");

    useEffect(() => {
        // Fetch categories from API with pagination
        dispatch(adminHandlers.fetchCategoriesHandler({ 
            page: currentPage, 
            size: itemsPerPage 
        }));
    }, [dispatch, currentPage, itemsPerPage])

    // Handle view
    const handleView = (category) => {
        setSelectedCategory(category);
        // Delay opening to allow modal to mount first
        setTimeout(() => {
            setIsModalOpen(true);
        }, 10);
    };

    // Handle modal close
    const handleModalClose = () => {
        setIsModalOpen(false);
        // Delay clearing selectedCategory to allow close animation
        setTimeout(() => {
            setSelectedCategory(null);
        }, 300);
    };

    // Handle category update from modal
    const handleCategoryUpdate = (updatedCategory) => {
        // Update the selected category with new data
        setSelectedCategory(updatedCategory);
        // Refresh the categories list
        dispatch(adminHandlers.fetchCategoriesHandler({ 
            page: currentPage, 
            size: itemsPerPage 
        }));
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Calculate total pages
    const totalPages = Math.ceil(categoriesTotal / itemsPerPage) || 1;

    const viewToggle = (
        <div className="flex items-center bg-charleston-green border border-zinc-700 rounded-full p-1">
            <button
                onClick={() => setViewMode("list")}
                aria-label="List view"
                className={`p-2 rounded-full transition-colors ${
                    viewMode === "list"
                        ? "bg-zinc-700 text-white"
                        : "text-zinc-500 hover:text-white"
                }`}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
            </button>
            <button
                onClick={() => setViewMode("card")}
                aria-label="Card view"
                className={`p-2 rounded-full transition-colors ${
                    viewMode === "card"
                        ? "bg-zinc-700 text-white"
                        : "text-zinc-500 hover:text-white"
                }`}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM13 6a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2h-3a2 2 0 01-2-2V6zM4 15a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2H6a2 2 0 01-2-2v-3zM13 15a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2h-3a2 2 0 01-2-2v-3z" />
                </svg>
            </button>
        </div>
    );

    return (
        <AdminLayout>
            <ActionBar title="Categories Overview" additionalActions={viewToggle}/>
            <Container>
                <div className="h-full flex flex-col justify-between">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 text-center">Error: {error.data?.message || "Failed to load categories"}</div>
                    ) : (
                        <>
                    {viewMode === "card" ? (
                        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6 content-start">
                            {categories.map((item) => (
                                <CategoryCard
                                    key={item.id}
                                    id={item.id}
                                    data={item}
                                    onView={() => handleView(item)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="mb-6">
                            <CategoryList data={categories} onView={handleView} />
                        </div>
                    )}
                    {categories.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={categoriesTotal}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                        />
                    )}
                        </>
                    )}
                </div>
            </Container>
            
            {/* Category View/Edit Modal */}
            {selectedCategory && (
                <CategoryViewModal
                    category={selectedCategory}
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    onUpdate={handleCategoryUpdate}
                />
            )}
        </AdminLayout>
    )
}

export default Categories
